/**
 * QR App - © 2025 Juan Pablo Marín Del Rincón. Todos los derechos reservados.
 *
 * Este sistema fue desarrollado exclusivamente para Dinámica Juvenil Sfa.
 * Se prohíbe su venta, redistribución, modificación o uso no autorizado,
 * total o parcial, sin consentimiento por escrito del autor.
 *
 * Protegido por la Ley Federal del Derecho de Autor (LFDA), Título VII:
 * - Capítulo I: Infracciones Administrativas
 * - Capítulo II: Delitos
 * - Capítulo III: Sanciones
 *
 * Cualquier uso indebido podrá ser sancionado conforme a la ley.
 * Contacto: jpmarin1999.@gmail.com
 */

import React, { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { sileo } from "sileo";
import {
  Form, Input, DatePicker, Select, Upload, Button, Checkbox,
  InputNumber, Card, Typography, Divider, Modal, message, Steps, AutoComplete
} from "antd";
import {
  UserOutlined, MailOutlined, LockOutlined, UploadOutlined, NumberOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import esES from "antd/es/date-picker/locale/es_ES";
import axios from "axios";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import { getBackendUrl } from "../../utils/env";

moment.locale("es");

const isMobile = window.innerWidth < 768;

const { Paragraph, Title } = Typography;
const { Option } = Select;

// ======================== CONSTANTES ========================
const SACRAMENTS = ["bautismo", "eucaristia", "confirmacion"];

const stepFields = [
  ["name", "apePat", "apeMat", "email"], // Step 0 – Datos
  ["password", "confirmPassword"],       // Step 1 – Seguridad
  ["birth_date", "gender", "dinamica", "sacraments"], // Step 2 – Info
  ["profile_picture", "terms", "recaptcha"], // Step 3 – Verificación
];


const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?]).{8,}$/;
const PASSWORD_LABELS = ["Muy débil", "Débil", "Media", "Buena", "Fuerte"];
const PASSWORD_COLORS = ["red", "orange", "gold", "blue", "green"];
const styles = {
  container: {
    minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center",
    background: "linear-gradient(to right, #fff8dc, #f0e6d2)", padding: "40px 16px",
  },
  card: {
    width: "100%", maxWidth: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", borderRadius: 12,
  },
  passwordBar: {
    height: "8px", backgroundColor: "#eee", borderRadius: "4px", overflow: "hidden",
  },
};

// ======================== FUNCIONES ========================
const getPasswordStrength = (password) => {
  const checks = [
    password.length >= 1,
    /\d/.test(password),
    /[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?]/.test(password),
    /[A-Za-z]/.test(password),
  ];
  const level = checks.filter(Boolean).length;
  return { level, label: PASSWORD_LABELS[level], color: PASSWORD_COLORS[level] };
};

const PasswordStrengthBar = ({ strength }) => (
  <div style={{ marginTop: 8 }}>
    <div style={styles.passwordBar}>
      <div
        style={{
          height: "100%", width: `${(strength.level / 3) * 100}%`,
          backgroundColor: strength.color, transition: "width 0.3s ease",
        }}
      />
    </div>
    <span style={{ fontSize: "12px", color: strength.color }}>
      Fortaleza: {strength.label}
    </span>
  </div>
);

const processImage = async (file) => {
  let processedFile = file;

  // 1. 🍏 Manejo de HEIC (iPhone)
  if (file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic")) {
    try {
      const blob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.8,
      });
      processedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
        type: "image/jpeg",
      });
    } catch (e) {
      console.error("Error convirtiendo HEIC", e);
    }
  }

  // 2. 📉 Compresión Universal (Para evitar el Error 400 por tamaño)
  const options = {
    maxSizeMB: 0.8,          // Forzamos a que pese menos de 1MB
    maxWidthOrHeight: 1200, // Redimensionamos si es una foto de 50MP
    useWebWorker: true,
  };

  try {
    const compressedBlob = await imageCompression(processedFile, options);
    return new File([compressedBlob], processedFile.name, { type: processedFile.type });
  } catch (e) {
    return processedFile; // Si falla la compresión, enviamos el original
  }
};

// ======================== COMPONENTE ========================
export default function Registro() {

  const stepRefs = {
    0: useRef(null),
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
  };
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!isMobile) return;

    const ref = stepRefs[step];
    if (ref?.current) {
      setTimeout(() => {
        ref.current.focus?.();
      }, 200);
    }
  }, [step, isMobile]);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [captchaValue, setCaptchaValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sacraments, setSacraments] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [password, setPassword] = useState("");
  const [emailOptions, setEmailOptions] = useState([]);

  const emailDomains = [
    "gmail.com",
    "hotmail.com",
    "outlook.com",
    "yahoo.com",
    "icloud.com",
  ];


  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList ?? [];
  };


  const handleEmailSearch = (value) => {
    if (!value) {
      setEmailOptions([]);
      return;
    }

    const [local, domain] = value.split("@");

    if (!domain) {
      // Antes del @
      setEmailOptions(
        emailDomains.map(d => ({ value: `${local}@${d}` }))
      );
    } else {
      // Después del @ (filtrado)
      setEmailOptions(
        emailDomains
          .filter(d => d.startsWith(domain))
          .map(d => ({ value: `${local}@${d}` }))
      );
    }
  };

  const handleSacramentsChange = (selected) =>
    setSacraments(selected.includes("all") ? SACRAMENTS : selected);

  const onFinish = async (values) => {
    // 1. Validaciones básicas de seguridad
    if (values.password !== values.confirmPassword)
        return message.error("Las contraseñas no coinciden");

    if (!values.birth_date) 
        return message.error("Selecciona tu fecha de nacimiento");

    if (values.birth_date.isAfter(moment())) 
        return message.error("La fecha de nacimiento no puede ser futura");

    if (!values.profile_picture || values.profile_picture.length === 0) 
        return message.error("Debes subir una foto de perfil");

    if (!captchaValue) 
        return message.error("Por favor verifica el CAPTCHA");

    try {
        setLoading(true);

        // --- 🚀 PROCESAMIENTO DE IMAGEN (Sentinel Shield) ---
        let originalFile = values.profile_picture[0].originFileObj;
        let fileToUpload = originalFile;

        // A. Convertir HEIC si es necesario
        if (originalFile.type === "image/heic" || originalFile.name.toLowerCase().endsWith(".heic")) {
            const convertedBlob = await heic2any({
                blob: originalFile,
                toType: "image/jpeg",
                quality: 0.8
            });
            fileToUpload = new File([convertedBlob], originalFile.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }

        // B. Comprimir para evitar Error 400 (Límite 1MB)
        const compressionOptions = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1200,
            useWebWorker: true
        };
        const compressedBlob = await imageCompression(fileToUpload, compressionOptions);
        fileToUpload = new File([compressedBlob], fileToUpload.name, { type: fileToUpload.type });
        // ---------------------------------------------------

        const formData = new FormData();
        Object.entries({
            apePat: values.apePat,
            apeMat: values.apeMat || "",
            name: values.name,
            email: values.email,
            password: values.password,
            birth_date: values.birth_date.format("YYYY-MM-DD"),
            sacraments: sacraments.join(","),
            dinamica: values.dinamica || "",
            gender: values.gender,
            recaptcha_token: captchaValue,
        }).forEach(([k, v]) => formData.append(k, v));

        // Adjuntamos el archivo ya procesado y ligero
        formData.append("profile_picture", fileToUpload);

        const { data } = await axios.post(
            `${getBackendUrl()}api/auth/register.php`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (data.success) {
            sileo.success({
                title: "Registro exitoso",
                fill: "#fadb14",
                duration: 5000
            });
            navigate("/");
        } else {
            sileo.error({
                title: data.message || "Error al registrar",
                fill: "#fadb14",
                duration: 5000
            });
        }

    } catch (err) {
        sileo.error({
            title: `Error: ${err.response?.data?.message || err.message || "Inténtalo más tarde"}`,
            duration: 5000
        });
    } finally {
        setLoading(false);
    }
};
  const strength = getPasswordStrength(password);

  return (
    <div style={styles.container}>
      <Card style={styles.card}>
        <Form layout="vertical" onFinish={onFinish} form={form} style={{ padding: 16 }}>
          <Title
            level={2}
            style={{
              textAlign: "center",
              marginBottom: 24,
              fontWeight: 700,
              color: "#faad14",
              letterSpacing: 0.5,
            }}
          >
            🚀 Registro de Usuario
          </Title>
          {isMobile && (
            <Steps current={step} size="small" style={{ marginBottom: 24 }}>
              <Steps.Step title="Datos" />
              <Steps.Step title="Seguridad" />
              <Steps.Step title="Info" />
              <Steps.Step title="Verificación" />
            </Steps>
          )}
          <div hidden={isMobile && step !== 0}>
            <>
              {/* DATOS PERSONALES */}
              <Divider titlePlacement="left" style={{ borderColor: "#fadb14" }}>
                Datos Personales
              </Divider>

              {[
                {
                  label: "Nombre",
                  name: "name",
                  prefix: <UserOutlined />,
                  rules: [{ required: true, message: "Ingresa tu nombre" }],
                  placeholder: "Ej. Pedro",
                },
                {
                  label: "Apellido Paterno",
                  name: "apePat",
                  prefix: <UserOutlined />,
                  rules: [{ required: true, message: "Ingresa tu apellido paterno" }],
                  placeholder: "Ej. López",
                },
                {
                  label: "Apellido Materno",
                  name: "apeMat",
                  prefix: <UserOutlined />,
                  placeholder: "Opcional",
                },
              ].map((f) => (
                <Form.Item
                  key={f.name}
                  label={f.label}
                  name={f.name}
                  hasFeedback
                  rules={f.rules}
                >
                  <Input
                    ref={stepRefs[0]}
                    prefix={f.prefix}
                    placeholder={f.placeholder}
                  />
                </Form.Item>
              ))}

              {/* CORREO CON AUTOCOMPLETE */}
              <Form.Item
                label="Correo"
                name="email"
                rules={[
                  { required: true, message: "Ingresa tu correo" },
                  { type: "email", message: "Ingresa un correo válido" },
                ]}
              >
                <AutoComplete
                  options={emailOptions}
                  onSearch={handleEmailSearch}
                >
                  <Input
                    prefix={<MailOutlined />}
                    autoCapitalize="none"
                    inputMode="email"
                  />
                </AutoComplete>
              </Form.Item>
            </>
          </div>

          <div hidden={isMobile && step !== 1}>
            <>
              <div>
                {/* SEGURIDAD */}
                <Divider titlePlacement="left" style={{ borderColor: "#fadb14" }}>Seguridad</Divider>
                <Form.Item
                  label="Contraseña"
                  name="password"
                  hasFeedback
                  rules={[
                    { required: true, message: "Ingresa tu contraseña" },
                    { pattern: PASSWORD_REGEX, message: "Mínimo 8 caracteres, incluyendo letras, números y un símbolo especial" },
                  ]}
                >
                  <Input.Password
                    ref={stepRefs[1]}
                    prefix={<LockOutlined />}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Item>
                {form.getFieldValue("password") && <PasswordStrengthBar strength={strength} />}

                <Form.Item
                  label="Confirmar Contraseña"
                  name="confirmPassword"
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    { required: true, message: "Confirma tu contraseña" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        return !value || getFieldValue("password") === value
                          ? Promise.resolve()
                          : Promise.reject(new Error("Las contraseñas no coinciden"));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />

                </Form.Item>
              </div>
            </>
          </div>

          <div hidden={isMobile && step !== 2}>
            <>
              {/* INFO ADICIONAL */}
              <Divider titlePlacement="left" style={{ borderColor: "#fadb14" }}>Información Adicional</Divider>
              <Form.Item label="Fecha de nacimiento" name="birth_date" hasFeedback rules={[{ required: true, message: "Selecciona tu fecha de nacimiento" }]}>
                <DatePicker
                  ref={stepRefs[2]}
                  format="DD/MM/YYYY"
                  locale={esES}
                  style={{ width: "100%" }}
                  placeholder="Selecciona tu fecha"
                  inputReadOnly
                  disabledDate={(current) => {
                    if (!current) return false;

                    const today = moment();
                    return (
                      current.isAfter(today.clone().subtract(17, "years")) ||
                      current.isBefore(today.clone().subtract(100, "years"))
                    );
                  }}
                />
              </Form.Item>
              <Form.Item label="Sexo" name="gender" hasFeedback rules={[{ required: true, message: "Selecciona tu sexo" }]}>
                <Select placeholder="Selecciona una opción">
                  <Option value="masculino">Masculino</Option>
                  <Option value="femenino">Femenino</Option>
                </Select>
              </Form.Item>
              <Form.Item label="# Dinámica" name="dinamica">
                <InputNumber prefix={<NumberOutlined />} placeholder="Número de dinámica (opcional)" style={{ width: "100%" }} />
              </Form.Item>

              {/* SACRAMENTOS */}
              <Form.Item label="Sacramentos recibidos">
                <Select mode="multiple" placeholder="Selecciona los sacramentos" value={sacraments} onChange={handleSacramentsChange}>
                  {SACRAMENTS.every(s => sacraments.includes(s)) || <Option value="all">Seleccionar todos</Option>}
                  {SACRAMENTS.map(s => (
                    <Option key={s} value={s}>
                      {s === "bautismo" ? "Bautismo" : s === "eucaristia" ? "Primera Comunión" : "Confirmación"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* FOTO */}
              <Form.Item
                name="profile_picture"
                label="Foto de Perfil"
                rules={[{ required: true, message: 'La foto es obligatoria' }]}
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  accept=".jpg,.jpeg,.png,.webp,.heic" // 👈 Añadimos .heic aquí
                  maxCount={1}
                  listType="picture"
                  beforeUpload={async (file) => {
                    // 🚀 Aquí ocurre la magia antes de guardar en el form
                    const readyFile = await processImage(file);

                    // Reemplazamos el archivo original por el optimizado
                    // Ant Design espera que devolvamos false para no subirlo al servidor aún
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                    Seleccionar Imagen
                  </Button>
                </Upload>
              </Form.Item>

            </>
          </div>
          <div hidden={isMobile && step !== 3}>
            <>

              {/* TÉRMINOS */}
              <Divider titlePlacement="left" style={{ borderColor: "#fadb14" }}>Términos y Condiciones</Divider>
              <Form.Item
                extra="Debes aceptar los términos y condiciones para registrarte"
                name="terms"
                valuePropName="checked"
                rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("Debes aceptar los términos")) }]}
              >
                <Checkbox>
                  Acepto los <a onClick={() => setIsModalVisible(true)}>Términos y Condiciones</a>
                </Checkbox>
              </Form.Item>

              {/* RECAPTCHA */}
              <Divider titlePlacement="left" style={{ borderColor: "#fadb14" }}>Verificación</Divider>
              <Form.Item extra="Verifica que no eres un robot"
                name="recaptcha"
                rules={[{ validator: () => captchaValue ? Promise.resolve() : Promise.reject(new Error("Por favor verifica el CAPTCHA")) }]}
              >
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(value) => setCaptchaValue(value)}
                  onExpired={() => setCaptchaValue(null)}
                />
              </Form.Item>

            </>
          </div>

          {/* BOTONES DE NAVEGACIÓN */}

          {!isMobile && (
            <Form.Item style={{ marginTop: 32 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  background: "#fadb14",
                  borderColor: "#fadb14",
                  color: "#000",
                  fontWeight: 700,
                }}
              >
                🚀 Registrarse
              </Button>
            </Form.Item>
          )}


          {isMobile && (
            <div style={{ display: "flex", gap: 8, marginTop: 24 }}>
              {step > 0 && (
                <Button block onClick={() => setStep(step - 1)}>
                  Atrás
                </Button>
              )}
              {step < 3 ? (
                <Button
                  type="primary"
                  block
                  onClick={async () => {
                    try {
                      await form.validateFields(stepFields[step]);
                      setStep(step + 1);
                    } catch {
                      // AntD ya muestra los errores, no hagas nada aquí
                    }
                  }}
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  style={{ background: "#fadb14", color: "#000" }}
                >
                  Registrarse
                </Button>
              )}
            </div>
          )}
          {/* LOGIN */}
          <Divider plain style={{ fontSize: "clamp(13px, 3.5vw, 15px)" }}>¿Ya tienes cuenta?</Divider>
          <div style={{ textAlign: "center" }}>
            <Button type="link" onClick={() => navigate("/")} icon={<UserOutlined />} style={{ fontSize: "clamp(14px, 4vw, 16px)" }}>
              Iniciar Sesión
            </Button>
          </div>

          {/* MODAL TÉRMINOS */}
          <Modal title="Términos y Condiciones" open={isModalVisible} onCancel={() => setIsModalVisible(false)} footer={null} width={600}>
            <Typography>
              <Title level={4}>Términos y Condiciones de Uso</Title>
              {/* Aquí dejas el texto completo como estaba */}
              <Paragraph>Los usuarios de este sistema desarrollado por 
                <strong>Juan Pablo Marín Del Rincón</strong>
                  aceptan expresamente a someterse y ajustarse a los siguientes, términos y condiciones, con el objetivo de evitar el plagio y la comisión de delitos por la propiedad intelectual, así como la privación de datos personales. </Paragraph>
                  <Paragraph><strong>1. Uso permitido: </strong> Este sistema está destinado exclusivamente para los participantes autorizados por <strong>Dinámica Juvenil SFA.</strong> Queda estrictamente prohibido su uso indebido o fraudulento. </Paragraph> 
                  <Paragraph><strong>2. Puntos (Bravos): </strong> Los puntos (denominados "Bravos") obtenidos a través del sistema no tienen valor monetario, no son transferibles y su administración queda a discreción de la organización.</Paragraph>
                  <Paragraph><strong>3. Privacidad: </strong> La información proporcionada se utilizará únicamente para fines de registro, control de actividades y comunicación interna. Tus datos no serán compartidos con terceros. </Paragraph> 
                  <Paragraph><strong>4. Propiedad intelectual: </strong> El sistema ha sido desarrollado por <strong>Juan Pablo Marín Del Rincón</strong>, mismo que no otorga ni autoriza el permiso para realizar algún uso distinto al señalado. Queda prohibida su copia, distribución, modificación o venta sin autorización expresa por el desarrollador a través de algún documento legal. </Paragraph> 
                  <Paragraph><strong>5.Sanciones: </strong> El uso indebido, manipulación de datos, generación de QR falsos u otras acciones que atenten contra la integridad del sistema podrán conllevar la suspensión, eliminación del usuario o acciones disciplinarias </Paragraph> 
                  <Paragraph><strong>6. Acciones legales: </strong> En casos graves, como clonación del sistema, intentos de sabotaje, uso no autorizado o fraude, el autor podrá ejercer acciones legales ante las autoridades competentes, incluyendo denuncias penales, demandas civiles, así como de las acciones necesarias para salvaguardar sus derechos personales y reales. </Paragraph> 
                  <Paragraph><strong>Queda prohibida la modificación, venta o distribución del sistema sin la autorización expresa y por escrito del autor. Todo uso indebido será sancionado conforme a la Ley Federal del Derecho de Autor en México así como las demás disposiciones legales que sean competentes. </strong>
                  </Paragraph> <Paragraph><strong>Última actualización: </strong> Agosto de 2025</Paragraph>
            </Typography>
          </Modal>
        </Form>
      </Card>
    </div>
  );
}
